import React, { createContext, useState, useContext, useCallback, useEffect, useMemo } from 'react';
import { CRM_CONFIG, VALID_COUNTRIES, getCountryConfig } from '../config/crmConfig';
import api from '../services/api';

const CountryContext = createContext(null);

const STORAGE_KEY = 'crm_active_country';

export const CountryProvider = ({ children, user }) => {
  const userLoaded = user != null;
  const accessCountry = String(user?.access_country || '').toUpperCase();

  const resolveInitial = () => {
    const stored = String(localStorage.getItem(STORAGE_KEY) || '').toUpperCase();
    if (VALID_COUNTRIES.includes(stored)) return stored;
    if (VALID_COUNTRIES.includes(accessCountry)) return accessCountry;
    return 'INDIA';
  };

  const [activeCountry, setActiveCountry] = useState(resolveInitial);

  useEffect(() => {
    if (!userLoaded) return;
    if (accessCountry === 'BOTH') {
      const stored = String(localStorage.getItem(STORAGE_KEY) || '').toUpperCase();
      if (VALID_COUNTRIES.includes(stored)) {
        setActiveCountry(stored);
      }
    } else {
      const locked = VALID_COUNTRIES.includes(accessCountry) ? accessCountry : 'INDIA';
      setActiveCountry(locked);
      localStorage.setItem(STORAGE_KEY, locked);
    }
  }, [userLoaded, accessCountry]);

  const switchCountry = useCallback((code) => {
    const upper = String(code || '').toUpperCase();
    if (!VALID_COUNTRIES.includes(upper)) return;
    if (userLoaded && accessCountry !== 'BOTH' && upper !== accessCountry) return;
    setActiveCountry(upper);
    localStorage.setItem(STORAGE_KEY, upper);
  }, [userLoaded, accessCountry]);

  const getConfig = useCallback(() => getCountryConfig(activeCountry), [activeCountry]);
  const canSwitch = userLoaded && accessCountry === 'BOTH';

  useEffect(() => {
    const id = api.interceptors.request.use((config) => {
      config.headers = config.headers || {};
      config.headers['X-Country'] = activeCountry;
      return config;
    });
    return () => api.interceptors.request.eject(id);
  }, [activeCountry]);

  const contextValue = useMemo(() => ({
    activeCountry,
    switchCountry,
    getConfig,
    canSwitch,
    config: CRM_CONFIG,
    allCountries: VALID_COUNTRIES,
  }), [activeCountry, switchCountry, getConfig, canSwitch]);

  return (
    <CountryContext.Provider value={contextValue}>
      {children}
    </CountryContext.Provider>
  );
};

export const useCountry = () => useContext(CountryContext);
export default CountryContext;
